import React, { useEffect } from 'react';

import { toastPool } from '../../toasts/models/toast-pool';
import { Toast } from '../../toasts/models/toast';
import { SingleSelectDropdown } from '../../../components/select-dropdown';

interface Props {
  onChange: (value: string) => void;
  value?: string;
  errorMessage?: string | null;
  hideLabel?: boolean;
  /**
   * Passed straight through to the kit's dropdown, which renders it instead of the default
   * bordered trigger line. The crew list uses it to make the role cell match the editable text
   * cells beside it; a form leaves it off and gets the standard field.
   */
  trigger?: (label?: string) => void;
}

interface Option {
  label: string;
  value: string;
}

const createOption = (value: string) => ({
  label: value,
  value: value,
});

export const RoleSelect: React.FC<Props> = ({
  onChange,
  value,
  errorMessage,
  hideLabel,
  trigger,
}) => {
  const [options, setOptions] = React.useState<Option[]>([]);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const response = await fetch('/data/film_crew_roles.csv', {
          headers: {
            'Cache-Control': 'public, max-age=15778476, immutable',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const csvText = await response.text();

        const roles = csvText
          .split('\n')
          .slice(1)
          .filter((role) => role.trim())
          .map((role) => createOption(role.trim()));

        // If we have a value that's not in the roles list, add it as a custom option
        if (value && !roles.some((role) => role.value === value)) {
          roles.push(createOption(value));
        }

        setOptions(roles);
      } catch (error) {
        console.error('Error loading roles:', error);
        toastPool.insert(new Toast('Failed to load roles list. Please try again later.', 'error'));

        if (value) {
          setOptions([{ label: value, value }]);
        }
      }
    };

    loadRoles();
  }, [value]);

  const handleChange = (newValue?: string) => {
    if (newValue) {
      onChange(newValue);
    }
  };

  const handleCreate = (inputValue: string) => {
    const newOption = createOption(inputValue);
    setOptions([...options, newOption]);
    onChange(newOption.value);
  };

  return (
    // The label is the kit's own now, not a hand-rolled `<label>` styled here — that copy is
    // what made this field disagree with the `TextInput`s beside it in the crew modal.
    <SingleSelectDropdown
      label={hideLabel ? undefined : 'Role'}
      title="Role"
      value={value}
      options={options}
      onValueChange={handleChange}
      onCreate={handleCreate}
      trigger={trigger}
      placeholder="Choose from the list or use your own"
    />
  );
};
